import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Music2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { slotStatsApi } from '../../lib/api';
import { classifyR14Error, r14ErrorText } from '../../lib/r14Errors';
import {
  normaliseTikTokStats, nothingRecorded, fmtCounter, fmtDuration, tiktokDays, tiktokKpi,
  TIKTOK_COUNTERS, TIKTOK_UNCONFIRMED, TIKTOK_CHART_SERIES, TIKTOK_WELCOME, TIKTOK_WELCOME_UNCONFIRMED_KEY, TIKTOK_REPOST,
  TIKTOK_REPOST_UNCONFIRMED_KEY, TIKTOK_OUTBOUND, TIKTOK_OUTBOUND_UNCONFIRMED_KEY, TIKTOK_POST, TIKTOK_POST_UNCONFIRMED_KEY,
} from '../../lib/tiktokStatsView';
import {
  fmtDay, fmtInt, fmtSpan, niceScale, tickFormatter, dateTicks, plural,
} from '../../lib/slotStatsView';
import {
  AxisFrame, ChartCard, DataTableView, DataEndBar, EmptyPanel, Legend, TooltipBox, AXIS_TICK, actionColor, yAxisWidth,
} from './ChartKit';
import { maxDateTicks } from './GrowthCharts';
import { CompactLine, Figure, logHidden, logFailed } from './R15Parts';

/*
 * r16 TT-3 (2026-10-03), redrawn analytics-grade in TT-6 (2026-10-04, the TT-5 review's must-fix 10 and the owner's rule: stats UI
 * has axes, labels, tooltips, legends and KPI tiles, checked on a rendered screenshot). The TikTok card of the Slot Stats page
 * (lib/tiktokStatsView.js has the server contract and the labels):
 *
 *   - GET /uiapi/slots/{slot}/tiktok-stats answers 404 / 405 (a server before r16 TT-3) or 404 not_enabled (switched off on the
 *     server): NOTHING is drawn. An Instagram slot (applies:false): nothing either.
 *   - A TikTok slot with nothing recorded yet: one compact line.
 *   - Otherwise:
 *       KPI tiles - likes, follows, videos watched, watch time: the last 7 days as the headline, today and the total under it; the
 *       taps that were not counted (unconfirmed likes / follows) in a tile of their own, never added to the done figures;
 *       a daily chart - likes and follows per day side by side (the page's action colours), titled axes, a legend, a tooltip per
 *       day with every counter of that day; a day between two recorded ones had no TikTok session and says so;
 *       its table twin (the Chart / Table switch) - every counter per day, newest first.
 *   A value the server did not send reads "-", never 0. What counts: a like when the next screen read proved it; a follow once the
 *   account's own Following count went up (the screen alone is not proof - TT-4 live).
 */

const show = (c, x) => {
  const s = fmtCounter(c, x);
  return s === null ? '-' : s;
};

const LIKES = TIKTOK_COUNTERS.find((c) => c.key === 'likes');
const FOLLOWS = TIKTOK_COUNTERS.find((c) => c.key === 'follows');
const VIDEOS = TIKTOK_COUNTERS.find((c) => c.key === 'videosWatched');
const WATCH = TIKTOK_COUNTERS.find((c) => c.key === 'watchSeconds');

/**
 * r16 TT-12: the welcome-DM tile - the DMs sent and confirmed in the last 7 days as the headline, today and the total under it; the ones
 * whose Send could not be confirmed are named after them (never added: they are not counted as sent).
 */
function WelcomeTile({ v }) {
  const k = tiktokKpi(v, TIKTOK_WELCOME.key);
  const u = v.total[TIKTOK_WELCOME_UNCONFIRMED_KEY];
  return (
    <Figure label={`${TIKTOK_WELCOME.label}, last 7 days`} testid="tiktok-stats-kpi-welcomeDms"
      value={k.last7 === null ? null : show(TIKTOK_WELCOME, k.last7)}
      caption={(
        <span className="tnum">
          Today {show(TIKTOK_WELCOME, k.today)} · total {show(TIKTOK_WELCOME, k.total)}
          {u ? <span data-testid="tiktok-stats-welcome-unconfirmed"> · {fmtInt(u)} not confirmed (not counted)</span> : null}
        </span>
      )} />
  );
}

/**
 * r16 TT-15: the repost tile - TikTok's own reposts confirmed in the last 7 days as the headline, today and the total under it; the ones whose
 * result could not be read are named after them (never added: they are not counted as reposts).
 */
function RepostTile({ v }) {
  const k = tiktokKpi(v, TIKTOK_REPOST.key);
  const u = v.total[TIKTOK_REPOST_UNCONFIRMED_KEY];
  return (
    <Figure label={`${TIKTOK_REPOST.label}, last 7 days`} testid="tiktok-stats-kpi-reposts"
      value={k.last7 === null ? null : show(TIKTOK_REPOST, k.last7)}
      caption={(
        <span className="tnum">
          Today {show(TIKTOK_REPOST, k.today)} · total {show(TIKTOK_REPOST, k.total)}
          {u ? <span data-testid="tiktok-stats-repost-unconfirmed"> · {fmtInt(u)} not confirmed (not counted)</span> : null}
        </span>
      )} />
  );
}

/**
 * r16 TT-23b: the post tile - the queued videos uploaded and confirmed in the last 7 days as the headline, today and the total under it; the ones
 * whose result could not be read are named after them (never added: they are not counted as posts).
 */
function PostTile({ v }) {
  const k = tiktokKpi(v, TIKTOK_POST.key);
  const u = v.total[TIKTOK_POST_UNCONFIRMED_KEY];
  return (
    <Figure label={`${TIKTOK_POST.label}, last 7 days`} testid="tiktok-stats-kpi-posts"
      value={k.last7 === null ? null : show(TIKTOK_POST, k.last7)}
      caption={(
        <span className="tnum">
          Today {show(TIKTOK_POST, k.today)} · total {show(TIKTOK_POST, k.total)}
          {u ? <span data-testid="tiktok-stats-post-unconfirmed"> · {fmtInt(u)} not confirmed (not counted)</span> : null}
        </span>
      )} />
  );
}

/**
 * r16 TT-17: the outbound-DM tile - outbound DMs confirmed in the last 7 days as the headline, today and the total under it; the ones whose Send
 * could not be confirmed are named after them (never added: they are not counted as sent).
 */
function OutboundTile({ v }) {
  const k = tiktokKpi(v, TIKTOK_OUTBOUND.key);
  const u = v.total[TIKTOK_OUTBOUND_UNCONFIRMED_KEY];
  return (
    <Figure label={`${TIKTOK_OUTBOUND.label}, last 7 days`} testid="tiktok-stats-kpi-outboundDms"
      value={k.last7 === null ? null : show(TIKTOK_OUTBOUND, k.last7)}
      caption={(
        <span className="tnum">
          Today {show(TIKTOK_OUTBOUND, k.today)} · total {show(TIKTOK_OUTBOUND, k.total)}
          {u ? <span data-testid="tiktok-stats-outbound-unconfirmed"> · {fmtInt(u)} not confirmed (not counted)</span> : null}
        </span>
      )} />
  );
}

/**
 * One KPI tile: the last 7 days as the headline (the period in the label, so a long value never wraps a suffix on a phone), today
 * and the total in the caption. A charted action carries its series swatch (identity rides on the mark, never on coloured text).
 */
function KpiTile({ counter, v, testid, color }) {
  const k = tiktokKpi(v, counter.key);
  return (
    <Figure label={(
      <span className="inline-flex items-center gap-1.5">
        {color && <span className="inline-block h-2 w-2 shrink-0 rounded-[2px]" style={{ background: color }} aria-hidden />}
        {counter.label}, last 7 days
      </span>
    )}
      testid={testid}
      value={k.last7 === null ? null : show(counter, k.last7)}
      caption={<span className="tnum">Today {show(counter, k.today)} · total {show(counter, k.total)}</span>} />
  );
}

/** The day's tooltip: the two charted actions first (value leads), then the day's other counters. */
function TikTokTip({ active, payload }) {
  if (!active) return null;
  const d = payload && payload[0] ? payload[0].payload : null;
  if (!d) return null;
  const title = fmtDay(d.date, 'long');
  if (!d.recorded) {
    return <TooltipBox title={title} rows={[{ key: 'none', value: 'No session', label: 'no TikTok session reported that day' }]} testid="tiktok-stats-tooltip" />;
  }
  const rows = TIKTOK_CHART_SERIES.map((s) => ({
    key: s.key, color: actionColor(s.key), value: fmtInt(d[s.key]), label: s.label.toLowerCase(),
  }));
  rows.push({ key: 'likesUnconfirmed', value: fmtInt(d.likesUnconfirmed), label: 'unconfirmed likes (not counted)', muted: true, medium: true });
  rows.push({ key: 'followsUnconfirmed', value: fmtInt(d.followsUnconfirmed), label: 'unconfirmed follows (not counted)', muted: true, medium: true });
  if (d.likesDropped) {
    // r16 TT-9: of the unconfirmed likes, the ones the like probation proved TikTok did not keep (likes were paused for the rest of that day)
    rows.push({ key: 'likesDropped', value: fmtInt(d.likesDropped), label: 'of them, likes TikTok dropped (likes paused that day)', muted: true, medium: true });
  }
  if (d.welcomeDms || d.welcomeDmsUnconfirmed) {
    // r16 TT-12: the welcome DMs of that day (the unconfirmed ones apart: not counted as sent)
    rows.push({ key: 'welcomeDms', value: fmtInt(d.welcomeDms || 0), label: 'welcome DMs sent', medium: true });
    if (d.welcomeDmsUnconfirmed) {
      rows.push({ key: 'welcomeDmsUnconfirmed', value: fmtInt(d.welcomeDmsUnconfirmed), label: 'welcome DMs not confirmed (not counted)', muted: true, medium: true });
    }
  }
  if (d.posts || d.postsUnconfirmed) {
    // r16 TT-23b: the posts of that day (the unconfirmed ones apart: not counted as posts)
    rows.push({ key: 'posts', value: fmtInt(d.posts || 0), label: 'posts (queued videos uploaded)', medium: true });
    if (d.postsUnconfirmed) {
      rows.push({ key: 'postsUnconfirmed', value: fmtInt(d.postsUnconfirmed), label: 'posts not confirmed (not counted)', muted: true, medium: true });
    }
  }
  if (d.reposts || d.repostsUnconfirmed) {
    // r16 TT-15: the reposts of that day (the unconfirmed ones apart: not counted as reposts)
    rows.push({ key: 'reposts', value: fmtInt(d.reposts || 0), label: 'reposts', medium: true });
    if (d.repostsUnconfirmed) {
      rows.push({ key: 'repostsUnconfirmed', value: fmtInt(d.repostsUnconfirmed), label: 'reposts not confirmed (not counted)', muted: true, medium: true });
    }
  }
  if (d.outboundDms || d.outboundDmsUnconfirmed) {
    // r16 TT-17: the outbound DMs of that day (the unconfirmed ones apart: not counted as sent)
    rows.push({ key: 'outboundDms', value: fmtInt(d.outboundDms || 0), label: 'outbound DMs', medium: true });
    if (d.outboundDmsUnconfirmed) {
      rows.push({ key: 'outboundDmsUnconfirmed', value: fmtInt(d.outboundDmsUnconfirmed), label: 'outbound DMs not confirmed (not counted)', muted: true,
        medium: true });
    }
  }
  rows.push({ key: 'videos', value: fmtInt(d.videosWatched), label: 'videos watched', medium: true });
  rows.push({ key: 'watch', value: fmtDuration(d.watchSeconds), label: 'watch time', medium: true });
  return (
    <TooltipBox title={title} rows={rows} testid="tiktok-stats-tooltip"
      footer={<p>{plural(d.sessions || 0, 'session')} {d.partial ? 'so far today: the day is not over' : 'that day'}</p>} />
  );
}

/** Likes and follows per day, side by side; one y axis (both are counts of actions). */
function TikTokDailyChart({ rows, height = 240, testid }) {
  const max = Math.max(0, ...rows.map((r) => Math.max(r.likes || 0, r.follows || 0)));
  const scale = niceScale(0, max, { count: 5, zero: true });
  const fmt = tickFormatter(scale.step, scale.hi);
  const days = rows.map((r) => r.date);
  const yW = yAxisWidth(scale.ticks, fmt);
  return (
    <AxisFrame yTitle="Actions per day" xTitle="Date" testid={testid}>
      {(width) => (
        <BarChart width={width} height={height} data={rows} margin={{ top: 12, right: 16, bottom: 0, left: 0 }}
          barCategoryGap="24%" barGap={2} role="img"
          aria-label={`TikTok likes and follows per day, ${fmtDay(days[0], 'medium')} to ${fmtDay(days[days.length - 1], 'medium')}`}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          <XAxis dataKey="date" ticks={dateTicks(days, maxDateTicks(width))} interval={0} tickFormatter={(d) => fmtDay(d)}
            tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8} />
          <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt} width={yW}
            tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={6} allowDataOverflow />
          <Tooltip filterNull={false} isAnimationActive={false} cursor={{ fill: 'var(--viz-hover)' }} content={(p) => <TikTokTip {...p} />} />
          {TIKTOK_CHART_SERIES.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={actionColor(s.key)} maxBarSize={14} isAnimationActive={false}
              shape={(p) => <DataEndBar {...p} vivid />} activeBar={(p) => <DataEndBar {...p} vivid isActive />} />
          ))}
        </BarChart>
      )}
    </AxisFrame>
  );
}

export default function TikTokStatsCard({ slotName }) {
  const q = useQuery({
    queryKey: ['tiktokStats', slotName],
    queryFn: () => slotStatsApi.tiktok(slotName).then((r) => r.data),
    retry: false,
  });
  if (q.isLoading) return null;
  if (q.error) {
    const c = classifyR14Error(q.error);
    if (c.kind === 'routeMissing' || c.kind === 'notEnabled') {
      logHidden('tiktok-stats', slotName, c.kind === 'notEnabled'
        ? `switched off on this server (${c.message || 'not_enabled'})` : `no /tiktok-stats route on this server (HTTP ${c.status})`);
      return null;
    }
    logFailed('tiktok-stats', slotName, c);
    return (
      <CompactLine icon={Music2} title="TikTok" testid="tiktok-stats-error" tone="error">
        {c.kind === 'slotNotFound' ? (c.message || `No slot ${slotName} on this account.`)
          : `The figures could not be loaded (${r14ErrorText(q.error, 'no answer')}).`}
      </CompactLine>
    );
  }
  const v = normaliseTikTokStats(q.data);
  if (!v) {
    logHidden('tiktok-stats', slotName, 'the answer carries no tiktok-stats fields');
    return null;
  }
  if (!v.applies) return null;
  if (nothingRecorded(v)) {
    return (
      <CompactLine icon={Music2} title="TikTok" testid="tiktok-stats-empty">
        No TikTok session has reported yet.
      </CompactLine>
    );
  }

  const rows = tiktokDays(v);
  const span = rows.length ? fmtSpan(rows[0].date, rows[rows.length - 1].date) : null;
  const spanSessions = rows.reduce((n, r) => n + (r.sessions || 0), 0);
  const noSessionDays = rows.filter((r) => !r.recorded).length;
  const running = rows.some((r) => r.partial && r.recorded);	// TT-7: today with no session yet draws no lighter bar
  const unconfirmed = (p) => {
    const a = v[p].likesUnconfirmed;
    const b = v[p].followsUnconfirmed;
    return a === null && b === null ? null : (a || 0) + (b || 0);
  };
  const totalUnconfirmed = unconfirmed('total');

  const tableColumns = [
    { key: 'date', label: 'Day', render: (r) => `${fmtDay(r.date, 'long')}${r.partial ? ' (so far)' : ''}` },
    ...[...TIKTOK_COUNTERS.filter((c) => c.key !== 'storyLikes'), ...TIKTOK_UNCONFIRMED].map((c) => ({
      key: c.key, label: c.label, align: 'right', render: (r) => (r.recorded ? show(c, r[c.key]) : '-'),
    })),
    // r16 TT-12: the welcome DMs per day (sent; not confirmed)
    { key: TIKTOK_WELCOME.key, label: TIKTOK_WELCOME.label, align: 'right', render: (r) => (r.recorded ? show(TIKTOK_WELCOME, r[TIKTOK_WELCOME.key]) : '-') },
    {
      key: TIKTOK_WELCOME_UNCONFIRMED_KEY, label: 'Welcome DMs not confirmed', align: 'right',
      render: (r) => (r.recorded ? show(null, r[TIKTOK_WELCOME_UNCONFIRMED_KEY]) : '-'),
    },
    // r16 TT-23b: the posts per day (confirmed; not confirmed)
    { key: TIKTOK_POST.key, label: TIKTOK_POST.label, align: 'right', render: (r) => (r.recorded ? show(TIKTOK_POST, r[TIKTOK_POST.key]) : '-') },
    {
      key: TIKTOK_POST_UNCONFIRMED_KEY, label: 'Posts not confirmed', align: 'right',
      render: (r) => (r.recorded ? show(null, r[TIKTOK_POST_UNCONFIRMED_KEY]) : '-'),
    },
    // r16 TT-15: the reposts per day (confirmed; not confirmed)
    { key: TIKTOK_REPOST.key, label: TIKTOK_REPOST.label, align: 'right', render: (r) => (r.recorded ? show(TIKTOK_REPOST, r[TIKTOK_REPOST.key]) : '-') },
    {
      key: TIKTOK_REPOST_UNCONFIRMED_KEY, label: 'Reposts not confirmed', align: 'right',
      render: (r) => (r.recorded ? show(null, r[TIKTOK_REPOST_UNCONFIRMED_KEY]) : '-'),
    },
    // r16 TT-17: the outbound DMs per day (confirmed; not confirmed)
    { key: TIKTOK_OUTBOUND.key, label: TIKTOK_OUTBOUND.label, align: 'right', render: (r) => (r.recorded ? show(TIKTOK_OUTBOUND, r[TIKTOK_OUTBOUND.key]) : '-') },
    {
      key: TIKTOK_OUTBOUND_UNCONFIRMED_KEY, label: 'Outbound DMs not confirmed', align: 'right',
      render: (r) => (r.recorded ? show(null, r[TIKTOK_OUTBOUND_UNCONFIRMED_KEY]) : '-'),
    },
    { key: 'sessions', label: 'Sessions', align: 'right', render: (r) => (r.recorded ? show(null, r.sessions) : 'no session') },
  ];

  return (
    <ChartCard icon={Music2} title="TikTok" testid="tiktok-stats-card"
      subtitle={(
        <span data-testid="tiktok-stats-subtitle">
          A like counts when the next screen read proved it (the day&apos;s first like: once the account&apos;s own Liked tab shows it 8-12 minutes later); a
          follow counts once the account&apos;s own Following count went up.
          {v.recordedSince ? ` Recorded since ${fmtDay(v.recordedSince, 'long')}.` : ''}
        </span>
      )}
      controls={(
        <div className="space-y-3">
          {/* r16 TT-15: seven tiles - four per row from xl (six in one row were 170 px and already wrapped their labels); r16 TT-17: eight; r16 TT-23b: nine */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4" data-testid="tiktok-stats-kpis">
            <KpiTile counter={LIKES} v={v} color={actionColor('likes')} testid="tiktok-stats-kpi-likes" />
            <KpiTile counter={FOLLOWS} v={v} color={actionColor('follows')} testid="tiktok-stats-kpi-follows" />
            <KpiTile counter={VIDEOS} v={v} testid="tiktok-stats-kpi-videosWatched" />
            <KpiTile counter={WATCH} v={v} testid="tiktok-stats-kpi-watchSeconds" />
            <WelcomeTile v={v} />
            <PostTile v={v} />
            <RepostTile v={v} />
            <OutboundTile v={v} />
            <Figure label="Not counted, in total" testid="tiktok-stats-kpi-unconfirmed"
              value={totalUnconfirmed === null ? null : fmtInt(totalUnconfirmed)} suffix="taps"
              caption={(
                <span className="tnum" data-testid="tiktok-stats-unconfirmed">
                  {TIKTOK_UNCONFIRMED.map((c, i) => (
                    <span key={c.key}>{i > 0 ? ' · ' : ''}{show(c, v.total[c.key])} {c.label.toLowerCase().replace('unconfirmed ', '')}</span>
                  ))}
                  {' '}not proven to land, or not kept by TikTok
                  {v.total.likesDropped ? (
                    <span data-testid="tiktok-stats-dropped">
                      {' '}({fmtInt(v.total.likesDropped)} {v.total.likesDropped === 1 ? 'like' : 'likes'} of them TikTok dropped: likes were paused for the rest of that day)
                    </span>
                  ) : null}
                </span>
              )} />
          </div>
          <h3 className="pt-1 text-sm font-semibold text-foreground" data-testid="tiktok-stats-chart-title">
            Likes and follows per day{span ? `, ${span}` : ''}
            <span className="ml-2 font-normal text-muted-foreground tnum" data-testid="tiktok-stats-span-sessions">
              {plural(spanSessions, 'session')} in {plural(rows.length, 'day')}
            </span>
          </h3>
        </div>
      )}
      legend={<Legend testid="tiktok-stats-legend" items={TIKTOK_CHART_SERIES.map((s) => ({ key: s.key, label: s.label, color: actionColor(s.key) }))} />}
      table={<DataTableView caption="TikTok counters per day" testid="tiktok-stats-days" columns={tableColumns} rows={[...rows].reverse()} />}
      footer={[
        running ? <p key="today" data-testid="tiktok-stats-today-note">The lighter bars are today so far: the day is not over.</p> : null,
        noSessionDays ? (
          <p key="gaps" data-testid="tiktok-stats-gap-note">
            {noSessionDays === 1 ? '1 day' : `${noSessionDays} days`} in this span had no TikTok session (no bars; the tooltip and the table say so).
          </p>
        ) : null,
        <p key="unconfirmed">
          Not counted: a tap whose landing the next read could not prove (an unconfirmed like or follow), and a like or follow the screen showed
          that TikTok did not keep (the account&apos;s own Liked tab stayed empty, or its Following count did not go up). Either may have landed,
          so it uses up the session&apos;s budget, but it is never counted as done.
        </p>,
        <p key="story" data-testid="tiktok-stats-story-note">
          A story like counts when the story&apos;s next read shows it liked; story likes run only while the slot&apos;s setting 1701 (Enable Story
          Likes) is on, and each one uses up their own per hour / day / week limits. Videos watched counts each video once per session (a video
          TikTok shows again adds watch time only): the For You warm-up, the watch before each like, the story scan and the 8-12 minutes before the
          day&apos;s first like is checked.
        </p>,
        <p key="welcome" data-testid="tiktok-stats-welcome-note">
          A welcome DM counts when the sent message reads back in the chat. Each new follower (Inbox, Activity &amp; new followers) gets at most
          one; the followers already there at the account&apos;s first check get none. A DM whose Send could not be confirmed is not counted, but it
          uses up the welcome-DM per hour / day limits and is never sent again.
        </p>,
        <p key="repost" data-testid="tiktok-stats-repost-note">
          A repost is TikTok&apos;s own Repost of a For You video (&lsquo;Repost to followers&rsquo;), made only for a video that passes the
          slot&apos;s reposting settings (a hashtag from its source list - hashtags only on TikTok, a hashtag TikTok shows cut does not count -
          the caption words, the likes and comments) - at most one a session, within the posts per day. It counts when the video reads back as
          reposted; one whose result could not be read is not counted, but it uses up a post of the day and the video is never reposted again.
        </p>,
        <p key="post" data-testid="tiktok-stats-post-note">
          A post is the account&apos;s next queued video (Instagram&apos;s post queue of the same account name - a TikTok session fills no queue of its own; one video per queued post) uploaded on TikTok
          with its caption, visible to everyone - at most one a session, within the posts per day; a session that posts reposts nothing. It counts
          when a new first video shows on the account&apos;s own profile; one whose result could not be read is not counted, but it uses up a post of
          the day and that queued post is never uploaded again.
        </p>,
        <p key="outbound" data-testid="tiktok-stats-outbound-note">
          An outbound DM goes to an account the worker visits for this slot - its target accounts and the followers of its competitor accounts,
          never a hashtag target - after the follow it makes there, or as the only action with setting 755 on, within the follow limits. It counts
          when the sent message reads back in the chat; one whose Send could not be confirmed is not counted but is never sent again. Each account
          gets at most one, and nobody gets a welcome DM and an outbound DM on the same day.
        </p>,
      ]}>
      {rows.length ? <TikTokDailyChart rows={rows} testid="tiktok-stats-chart" />
        : <EmptyPanel testid="tiktok-stats-chart-empty">No day recorded yet.</EmptyPanel>}
    </ChartCard>
  );
}
